import { AI_EVAL } from '../../config/ai.ts'
import { BOARD_SIZE } from '../../config/board.ts'
import {
  checkWin,
  computeScore,
  inBounds,
  isBoardFull,
  placeStone,
} from '../engine.ts'
import { scanFullBoard } from '../threat-scan.ts'
import type { Board, Color, Move, Player } from './types.ts'
import { pickBest } from './types.ts'

const WIN_SCORE = AI_EVAL.WIN
const NEIGHBOR_RADIUS = 2

function opponent(c: Color): Color {
  return c === 'black' ? 'white' : 'black'
}

// 盤面全体の評価（color 視点）: 自分の攻撃 - 相手の防御要求 + スコア差
//
// 2026-04-17: 連続石ベース (analyzeDirection) から 5連窓スキャン (scanFullBoard) に
// 切り替えた。飛び4 などギャップを含む脅威を漏らさず拾うための対策（AI_EVAL のコメント参照）。
function staticEval(board: Board, color: Color): number {
  const opp = opponent(color)
  if (checkWin(board, color)) return WIN_SCORE
  if (checkWin(board, opp)) return -WIN_SCORE

  const mineCounts = scanFullBoard(board, color)
  const theirsCounts = scanFullBoard(board, opp)

  const mine =
    mineCounts.four * AI_EVAL.FOUR +
    mineCounts.three * AI_EVAL.THREE +
    mineCounts.two * AI_EVAL.TWO
  const theirs =
    theirsCounts.four * AI_EVAL.BLOCK_FOUR +
    theirsCounts.three * AI_EVAL.BLOCK_THREE +
    theirsCounts.two * AI_EVAL.BLOCK_TWO

  const scoreBlack = computeScore(board, 'black').total
  const scoreWhite = computeScore(board, 'white').total
  const scoreDiff = color === 'black' ? scoreBlack - scoreWhite : scoreWhite - scoreBlack

  return mine - theirs + scoreDiff * AI_EVAL.SCORE_GAIN_WEIGHT
}

// 既存石から NEIGHBOR_RADIUS マス以内の空マスを候補にする
function candidateMoves(board: Board, forbidden?: ReadonlySet<number>): Move[] {
  const seen = new Uint8Array(BOARD_SIZE * BOARD_SIZE)
  const moves: Move[] = []
  let hasStones = false

  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (board[r][c] === 'empty') continue
      hasStones = true
      for (let dr = -NEIGHBOR_RADIUS; dr <= NEIGHBOR_RADIUS; dr++) {
        for (let dc = -NEIGHBOR_RADIUS; dc <= NEIGHBOR_RADIUS; dc++) {
          const nr = r + dr
          const nc = c + dc
          if (!inBounds(nr, nc)) continue
          if (board[nr][nc] !== 'empty') continue
          const key = nr * BOARD_SIZE + nc
          if (seen[key]) continue
          if (forbidden?.has(key)) continue
          seen[key] = 1
          moves.push({ row: nr, col: nc })
        }
      }
    }
  }

  if (!hasStones) {
    const center = Math.floor(BOARD_SIZE / 2)
    const centerKey = center * BOARD_SIZE + center
    if (!forbidden?.has(centerKey)) {
      return [{ row: center, col: center }]
    }
    // 中央禁止時は中央隣接 8 マスを候補とする
    const out: Move[] = []
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue
        const nr = center + dr
        const nc = center + dc
        if (!inBounds(nr, nc)) continue
        if (forbidden?.has(nr * BOARD_SIZE + nc)) continue
        out.push({ row: nr, col: nc })
      }
    }
    return out
  }
  return moves
}

function minimax(
  board: Board,
  myColor: Color,
  toMove: Color,
  depth: number,
  alpha: number,
  beta: number,
  forbidden?: ReadonlySet<number>
): number {
  if (checkWin(board, myColor)) return WIN_SCORE
  if (checkWin(board, opponent(myColor))) return -WIN_SCORE
  if (depth === 0 || isBoardFull(board)) return staticEval(board, myColor)

  const moves = candidateMoves(board, forbidden)
  const isMax = toMove === myColor

  if (isMax) {
    let best = -Infinity
    for (const m of moves) {
      const next = placeStone(board, m.row, m.col, toMove)
      const score = minimax(next, myColor, opponent(toMove), depth - 1, alpha, beta, forbidden)
      if (score > best) best = score
      if (best > alpha) alpha = best
      if (alpha >= beta) break
    }
    return best
  } else {
    let best = Infinity
    for (const m of moves) {
      const next = placeStone(board, m.row, m.col, toMove)
      const score = minimax(next, myColor, opponent(toMove), depth - 1, alpha, beta, forbidden)
      if (score < best) best = score
      if (best < beta) beta = best
      if (alpha >= beta) break
    }
    return best
  }
}

// depth は「自分が何 ply 先まで読むか」（2 なら 自分1手 + 相手1手 の2ply）
export function createMinimaxPlayer(depth: number): Player {
  return {
    id: `minimax${depth}`,
    name: `Minimax depth=${depth}`,
    decide(board, color, { rng, forbidden, allowedMoves }) {
      const opp = opponent(color)
      const moves =
        allowedMoves && allowedMoves.length > 0
          ? Array.from(allowedMoves)
          : candidateMoves(board, forbidden)
      if (moves.length === 0) return null

      const candidates: { score: number; item: Move }[] = []
      for (const m of moves) {
        const next = placeStone(board, m.row, m.col, color)
        if (checkWin(next, color)) {
          candidates.push({ score: WIN_SCORE, item: m })
          continue
        }
        // 自分が1手置いた後、残り depth-1 ply を相手から開始
        const score = minimax(next, color, opp, depth - 1, -Infinity, Infinity, forbidden)
        candidates.push({ score, item: m })
      }
      return pickBest(candidates, rng)
    },
  }
}
